import { useEffect, useId, useState } from 'react';
import { Button, TextButton } from '../ui';
import { OtpInput } from './OtpInput';
import { useCodeVerification } from '../../../lib/use-code-verification';
import { wixLogout } from '../../../lib/logout';
import { PHONE_CONFLICT_CARE_URL } from '../../../lib/customer-care';
import { useTranslation } from '../../../lib/i18n';
import { handleModalEscape, pushModalEscape } from '../../../lib/modal-escape';
import styles from './CodeScreen.module.css';

type CodeVerification = ReturnType<typeof useCodeVerification>;

// The code step's fields. Rendered only by the verification dialog now — the full-screen version is gone
// and `/onboarding/code` forwards to `/welcome` (see onboarding-gate's RETIRED_ONBOARDING). The hook is
// passed in rather than called again: the dialog's heading needs the number it resolves, and a second
// call would mount a second, independent copy of the step's state.
export function CodeStepBody({ code }: { code: CodeVerification }) {
  const { t } = useTranslation();
  const sending = code.resend === 'sending';
  // One wait, both channels. Alternating SMS → WhatsApp → SMS would otherwise walk straight through a
  // per-button cooldown and send three codes in as many seconds, which is the thing being prevented —
  // and the server throttles the CALLER, so the second channel would only earn a 429 anyway.
  const waiting = code.resendIn > 0;
  // The wait is shown on the buttons themselves rather than as a standing line under them. Open on
  // hover AND focus, which is what makes it reachable without a mouse: a keyboard user tabs to it, and
  // a touch user — who has no hover at all — focuses it by tapping, since the control below is
  // `aria-disabled` rather than `disabled` and so is still focusable.
  // Hover and focus are tracked SEPARATELY and the hint is up while either holds. Sharing one flag meant
  // the pointer wandering off the row closed a hint the keyboard had opened, so a keyboard user lost the
  // only explanation for the grey without touching anything.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // A dismissal answers ONE wait. The only things that lift it are a fresh mouseenter or focus, and both
  // handlers are attached only while a wait runs — so with the pointer resting on the row, or focus
  // staying on the button the user pressed Enter on (or clicked, in Safari, which does not focus a
  // button), the NEXT wait arrived with both buttons grey and no reason stated, since nothing re-entered.
  // Every new wait therefore starts undismissed.
  useEffect(() => {
    if (waiting) setDismissed(false);
  }, [waiting]);
  const hintOpen = (hovered || focused) && !dismissed;
  // Only the TOOLTIP stands down while the 429's own message is up, and only for THAT error: it counts
  // the same seconds itself, so showing both states the wait twice. Every other error — a wrong code,
  // most of all — says nothing about the wait, so the hint is then the only thing explaining the grey
  // and must stay.
  //
  // `aria-disabled` is deliberately NOT gated on this. It tracks the cooldown and nothing else, because
  // the press is refused by `sendCode` against the deadline whatever is on screen: dropping it while a
  // wrong-code error showed left both buttons looking live and silently doing nothing, which is worse
  // than the standing line this replaced.
  const hintable = waiting && code.errorKind !== 'tooSoon';
  // What is actually ON SCREEN, and the only thing the Escape layer may be tied to. `hintOpen` alone
  // outlives the tooltip: the pointer sits on the row, the wait runs out (or a 429 takes `hintable`
  // away), the span stops rendering — and an invisible layer would go on eating the first Escape, so
  // the reader has to press it twice to close the step. Nothing visible, nothing to dismiss.
  const hintShown = hintable && hintOpen;
  // Per INSTANCE, not a constant: `CodeStepBody` is the phone modal's body AND the post-purchase
  // takeover's, and `AppFrame` can have both mounted. Two elements sharing one id makes
  // `aria-describedby` resolve to whichever came first in the DOM, so the takeover a user is looking at
  // would announce the dialog's wait underneath it.
  const waitHintId = useId();
  const waitHint = t('onboarding.code.symphony.resendIn', { seconds: code.resendIn });
  // Hover/focus on EITHER button opens the one hint: both are gated by the same wait, so two tooltips
  // saying the same seconds would be noise.
  // WCAG 1.4.13 wants hover/focus content dismissible without moving the pointer or losing focus — and
  // dismissing a tooltip must not also close the step it sits in. The dialog claims Escape on `window`
  // in the capture phase and delegates to the INNERMOST layer (see lib/modal-escape.ts), so the hint
  // registers as a layer of its own while it is up: the first Escape takes the hint, a second reaches
  // the dialog. A listener of our own could not win that race — the dialog's runs first and calls
  // `stopImmediatePropagation`.
  useEffect(() => {
    if (!hintShown) return;
    // Dismissing has to outrank "still hovered / still focused", or Escape would be undone on the next
    // render by the very state that opened it.
    const pop = pushModalEscape(() => setDismissed(true));
    // `pushModalEscape` only joins the stack; the LISTENER that feeds it belongs to the modal shell.
    // `PostPurchasePhoneGate` renders this body on a full-screen takeover with no Modal at all, so on
    // that surface nothing would ever dispatch and Escape would not dismiss the hint. Installing the
    // same dispatcher the shell installs is safe in both places: `handleModalEscape` serves only the
    // innermost layer and calls `stopImmediatePropagation`, so whichever window listener runs first
    // handles it once and the other never runs.
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, [hintShown]);

  const hintProps = {
    // The whole cooldown, every time — this is what greys the button and keeps it focusable.
    ...(waiting ? { 'aria-disabled': true as const } : {}),
    // Only the DESCRIPTION is conditional — there is nothing to describe when no hint can show.
    ...(hintable ? { 'aria-describedby': waitHintId } : {}),
    // The focus trackers are NOT. Gating a handler on `hintable` drops the matching leave event: when a
    // wait ends the pair detaches, and a blur that happens after that is never seen, so `focused` stays
    // true for a button nobody is on. The next wait clears `dismissed` and the tooltip opens against
    // that stale flag, with no control hovered or focused. Tracking is cheap and is only ever READ
    // through `hintShown`, which already asks `hintable` — so the flags may always be live.
    onFocus: () => { setFocused(true); setDismissed(false); },
    onBlur: () => setFocused(false),
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        code.submit();
      }}
    >
      <OtpInput code={code} invalid={code.errorKind === 'code'} />

      {code.error && !code.phoneInUse && (
        <p className={styles.error} role="alert">
          {code.error}
        </p>
      )}

      {code.phoneInUse ? (
        <div className={styles.inUse} role="alert">
          <p className={styles.inUseTitle}>{t('onboarding.code.phoneInUse.title')}</p>
          <p className={styles.inUseBody}>{t('onboarding.code.phoneInUse.body')}</p>
          <div className={styles.inUseActions}>
            <Button
              variant="outlined"
              onClick={() => {
                code.logPageClickBi('phone_in_use_use_another');
                code.toPhoneStep();
              }}
            >
              {t('onboarding.code.phoneInUse.useAnother')}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                code.logPageClickBi('phone_in_use_sign_in_other');
                wixLogout();
              }}
            >
              {t('onboarding.code.phoneInUse.signInOther')}
            </Button>
            {/* The third way out, and the only one that helps the person whose number is already on the
                account they are trying to get INTO: for them "use another number" and "sign in with
                another email" are both wrong answers, and what is left is a human. Same hand-off the two
                phones offer on the same failure (`SupportHandoff` there). */}
            <Button
              variant="text"
              onClick={() => {
                code.logPageClickBi('phone_in_use_contact_support');
                window.open(PHONE_CONFLICT_CARE_URL, '_blank', 'noopener,noreferrer');
              }}
            >
              {t('onboarding.code.phoneInUse.contactSupport')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.verify}>
            <Button size="large" fullWidth disabled={!code.complete} loading={code.submitting} onClick={code.submit}>
              {t('onboarding.code.symphony.cta')}
            </Button>
          </div>

          {/* Both are disabled while either send is in flight — otherwise the idle one looks live and
              swallows the click. The WAIT is different: it uses `aria-disabled`, so the button keeps the
              DS's disabled look (`.root[aria-disabled='true']` is styled with `:disabled`) while staying
              focusable. A real `disabled` would drop it out of the tab order, and the tooltip carrying
              the only reason the button is grey would be unreachable by keyboard and by touch.
              The press itself is refused by `sendCode`, which reads the deadline. */}
          {/* The POINTER half lives on the wrapper, not on the buttons: the tooltip renders inside it,
              so the pointer can travel from a button onto the tooltip without it closing — WCAG 1.4.13
              requires hover content to be hoverable. Focus stays per-button, which is what gives the
              `aria-describedby` association. */}
          <div
            className={styles.resend}
            onMouseEnter={() => { setHovered(true); setDismissed(false); }}
            onMouseLeave={() => setHovered(false)}
          >
            <Button
              variant="outlined"
              size="large"
              disabled={sending}
              loading={sending && code.method !== 'whatsapp'}
              onClick={() => void code.sendCode('sms', 'resend_sms')}
              {...hintProps}
            >
              {t('onboarding.code.symphony.resend.sms')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              disabled={sending}
              loading={sending && code.method === 'whatsapp'}
              onClick={() => void code.sendCode('whatsapp', 'resend_whatsapp')}
              {...hintProps}
            >
              {t('onboarding.code.symphony.resend.whatsapp')}
            </Button>

            {/* The wait, as the buttons' own description, and a child of this row so it anchors to it.
                Always in the DOM while the wait runs — a screen reader reads it from `aria-describedby`
                on focus, once, rather than re-announcing a number that changes every second — and
                visually revealed only while hovered or focused. Out of the layout either way, so the
                card never reflows.

                There is deliberately no "a new code is on its way" line: the buttons going grey again IS
                that confirmation, and a message saying the same thing was the part that read as clutter. */}
            {hintable ? (
              <span
                id={waitHintId}
                role="tooltip"
                className={hintShown ? styles.waitHintOpen : styles.waitHint}
              >
                {waitHint}
              </span>
            ) : null}
          </div>

          <div className={styles.footer}>
            <TextButton
              size="small"
              underline
              onClick={() => {
                code.logPageClickBi('use_different_number');
                code.toPhoneStep();
              }}
            >
              {t('onboarding.code.useDifferentNumber')}
            </TextButton>
          </div>
        </>
      )}
    </form>
  );
}
